import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertTriangle, PenLine, Type, Eraser } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Trim empty canvas space so the signature fills box 27
const cropped = (canvas) => {
  const { width, height } = canvas;
  const px = canvas.getContext('2d').getImageData(0, 0, width, height).data;
  let [x0, y0, x1, y1] = [width, height, 0, 0];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (px[(y * width + x) * 4 + 3]) {
        if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
      }
    }
  }
  if (x1 <= x0 || y1 <= y0) return canvas.toDataURL('image/png');
  const pad = 6;
  const out = document.createElement('canvas');
  out.width = x1 - x0 + pad * 2;
  out.height = y1 - y0 + pad * 2;
  out.getContext('2d').drawImage(canvas, x0 - pad, y0 - pad, out.width, out.height, 0, 0, out.width, out.height);
  return out.toDataURL('image/png');
};

export const SignaturePad = ({ onChange }) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  useEffect(() => {
    const c = ref.current;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio;
    c.height = c.offsetHeight * ratio;
    const ctx = c.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0b1f5c';
  }, []);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };
  const down = (e) => {
    drawing.current = true;
    ref.current.setPointerCapture(e.pointerId);
    const ctx = ref.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(...pos(e));
  };
  const move = (e) => {
    if (!drawing.current) return;
    const ctx = ref.current.getContext('2d');
    ctx.lineTo(...pos(e));
    ctx.stroke();
    dirty.current = true;
  };
  const up = () => {
    drawing.current = false;
    if (dirty.current) onChange(cropped(ref.current));
  };
  const clear = () => {
    const c = ref.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    dirty.current = false;
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={ref}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerLeave={up}
        className="w-full h-40 border-2 border-dashed border-gray-300 rounded-lg bg-white touch-none cursor-crosshair"
        data-testid="signature-canvas"
      />
      <button type="button" onClick={clear} className="mt-2 text-sm text-gray-600 flex items-center gap-1 hover:text-gray-900" data-testid="signature-clear-btn">
        <Eraser className="w-4 h-4" /> Clear
      </button>
    </div>
  );
};

export const MethodTabs = ({ method, setMethod }) => (
  <div className="flex gap-2 mb-3">
    {[['drawn', 'Draw signature', PenLine], ['typed', 'Type name', Type]].map(([m, label, Icon]) => (
      <button key={m} type="button" onClick={() => setMethod(m)} data-testid={`sign-method-${m}`}
        className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors ${method === m ? 'bg-gray-900 text-[#d4af37]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
        <Icon className="w-4 h-4" /> {label}
      </button>
    ))}
  </div>
);

export default function SignPlanOfCarePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [method, setMethod] = useState('drawn');
  const [image, setImage] = useState(null);
  const [typedName, setTypedName] = useState('');
  const [attest, setAttest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState('');

  useEffect(() => {
    (async () => {
      const res = await fetch(`${API_URL}/api/esign/${token}`);
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'This link is not valid'); return; }
      setInfo(data);
      setTypedName(data.physicianName || '');
      const pdf = await fetch(`${API_URL}/api/esign/${token}/pdf`);
      if (pdf.ok) setPdfUrl(URL.createObjectURL(await pdf.blob()));
    })();
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/esign/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ method, typedName, signatureImage: image, attest }),
      });
      const data = await res.json();
      if (!res.ok) setError(data.detail || 'Signing failed');
      else setDone(data.message);
    } catch (e) {
      setError(`Could not reach the server: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const ready = attest && (method === 'drawn' ? !!image : typedName.trim().length >= 3);

  if (done) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-6" data-testid="sign-success">
        <div className="bg-white rounded-2xl p-10 max-w-md text-center">
          <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Signed</h1>
          <p className="text-gray-600">{done}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 py-8 px-4" data-testid="sign-485-page">
      <div className="max-w-5xl mx-auto">
        <header className="mb-6">
          <p className="text-[#d4af37] text-sm tracking-widest uppercase">{info?.agency || 'IVORY Home Health Care'}</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mt-1">Plan of Care Signature</h1>
          {info && <p className="text-gray-400 mt-2">CMS-485 for <span className="text-white font-medium">{info.patientName}</span>, requested for {info.physicianName}</p>}
          {pdfUrl && (
            <a href={pdfUrl} target="_blank" rel="noreferrer" download="CMS-485.pdf" className="inline-block mt-3 text-sm text-[#d4af37] underline" data-testid="sign-open-pdf-link">
              Open or download the full Plan of Care PDF
            </a>
          )}
        </header>

        {error && !info && (
          <div className="bg-white rounded-xl p-8 flex items-center gap-3 text-red-700" data-testid="sign-error">
            <AlertTriangle className="w-6 h-6" /> {error}
          </div>
        )}

        {info && (
          <div className="grid lg:grid-cols-[1fr_380px] gap-6">
            <div className="bg-white rounded-xl overflow-hidden h-[75vh]">
              {pdfUrl ? <iframe title="CMS-485" src={pdfUrl} className="w-full h-full" data-testid="sign-pdf-preview" />
                : <div className="h-full flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}
            </div>
            <div className="bg-white rounded-xl p-5 h-fit">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Sign box 27</h2>
              <MethodTabs method={method} setMethod={setMethod} />
              {method === 'drawn' ? <SignaturePad onChange={setImage} /> : (
                <div>
                  <input value={typedName} onChange={(e) => setTypedName(e.target.value)} data-testid="sign-typed-name-input"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#d4af37] outline-none" placeholder="Full name, credentials" />
                  {typedName && <p className="mt-3 text-3xl text-[#0b1f5c]" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{typedName}</p>}
                </div>
              )}
              <label className="flex items-start gap-2 mt-4 text-sm text-gray-700">
                <input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} className="mt-1" data-testid="sign-attest-checkbox" />
                I certify this patient is confined to the home and needs the services in this plan of care, and I am applying my electronic signature to this CMS-485.
              </label>
              {error && <p className="mt-3 text-sm text-red-600" data-testid="sign-submit-error">{error}</p>}
              <button type="button" disabled={!ready || submitting} onClick={submit} data-testid="sign-submit-btn"
                className="mt-4 w-full py-3 rounded-full bg-[#d4af37] text-gray-900 font-semibold disabled:opacity-40 hover:bg-[#c9a227] transition-colors flex items-center justify-center gap-2">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Sign and Return
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
